import React, { useEffect, useMemo, useState } from "react";
import { ClipboardCopy, Download, FileDown, QrCode } from "lucide-react";
import { Link } from "react-router-dom";
import {
  AI_SURVEY_AGES,
  AI_SURVEY_AREAS,
  AI_SURVEY_ID,
  WORKSHOP_INTEREST,
} from "./aiDigitalSkillsConfig";
import {
  countResponseValues,
  createFundingEvidenceSummary,
  filterAiSurveyResponses,
  getAiSurveyMetrics,
} from "./aiDigitalSkillsReporting";
import { aiSurveyResponsesToCsv } from "./csv";
import { loadResponses } from "./service";
import "./communityVoice.css";

const initialFilters = {
  riverside: "",
  area: "",
  age: "",
  interest: "",
  dateFrom: "",
  dateTo: "",
};

function Breakdown({ title, data }) {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  const largest = entries[0]?.[1] || 1;
  return (
    <section className="cv-card">
      <h2>{title}</h2>
      {entries.length ? entries.map(([label, count]) => (
        <div className="cv-bar" key={label}>
          <span>{label}</span>
          <strong>{count}</strong>
          <i style={{ width: `${Math.round((count / largest) * 100)}%` }} />
        </div>
      )) : <p>No responses in this selection.</p>}
    </section>
  );
}

function downloadText(content, filename) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

export default function AiDigitalSkillsAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [filters, setFilters] = useState(initialFilters);
  const [copyState, setCopyState] = useState("");

  useEffect(() => {
    loadResponses(AI_SURVEY_ID)
      .then(setRows)
      .catch(() => setLoadError("Survey responses could not be loaded. Please try again."))
      .finally(() => setLoading(false));
  }, []);

  const evidenceRows = useMemo(() => rows.filter((row) => !row.isTest), [rows]);
  const shown = useMemo(() => filterAiSurveyResponses(evidenceRows, filters), [evidenceRows, filters]);
  const metrics = useMemo(() => getAiSurveyMetrics(shown), [shown]);
  const summary = useMemo(() => createFundingEvidenceSummary(shown), [shown]);
  const updateFilter = (event) => setFilters((current) => ({ ...current, [event.target.name]: event.target.value }));

  const exportCsv = (includeContact) => {
    downloadText(
      aiSurveyResponsesToCsv(shown, { includeContact }),
      includeContact
        ? "uun-ai-digital-skills-survey-full-authorised.csv"
        : "uun-ai-digital-skills-survey-anonymised.csv"
    );
  };

  const copySummary = async () => {
    try {
      await navigator.clipboard.writeText(summary);
      setCopyState("Funding evidence summary copied.");
    } catch {
      setCopyState("Copy failed. Select the summary text and copy it manually.");
    }
  };

  return (
    <div className="cv-admin">
      <div className="cv-admin-title">
        <div>
          <p className="cv-kicker">Admin · Community Voice</p>
          <h1>AI &amp; Digital Skills Survey</h1>
          <p>Live evidence from the Community AI, Coding &amp; Digital Skills Survey.</p>
        </div>
        <Link className="cv-button cv-secondary" to="/admin/community-voice">Festival survey results</Link>
      </div>

      <section className="cv-card cv-admin-assets">
        <div>
          <h2>Survey promotion</h2>
          <p>Use the exact UUN survey link and ready-made print assets.</p>
        </div>
        <div className="cv-inline-actions">
          <a className="cv-button cv-secondary" href="/uun-ai-digital-skills-survey-qr.png" download><QrCode size={18} /> QR PNG</a>
          <a className="cv-button cv-secondary" href="/uun-ai-digital-skills-survey-qr.svg" download><QrCode size={18} /> QR SVG</a>
          <a className="cv-button" href="/uun-ai-digital-skills-survey-poster-a4.pdf" download><FileDown size={18} /> A4 poster</a>
        </div>
      </section>

      <div className="cv-filters" aria-label="Survey report filters">
        <label>Riverside resident<select name="riverside" value={filters.riverside} onChange={updateFilter}><option value="">All responses</option><option value="Yes">Yes</option><option value="No">No</option><option value="Not sure">Not sure</option><option value="Prefer not to say">Prefer not to say</option></select></label>
        <label>Area<select name="area" value={filters.area} onChange={updateFilter}><option value="">All areas</option>{AI_SURVEY_AREAS.map((area) => <option key={area}>{area}</option>)}</select></label>
        <label>Age group<select name="age" value={filters.age} onChange={updateFilter}><option value="">All ages</option>{AI_SURVEY_AGES.map((age) => <option key={age}>{age}</option>)}</select></label>
        <label>Interest level<select name="interest" value={filters.interest} onChange={updateFilter}><option value="">All levels</option>{WORKSHOP_INTEREST.map((interest) => <option key={interest}>{interest}</option>)}</select></label>
        <label>From date<input type="date" name="dateFrom" value={filters.dateFrom} onChange={updateFilter} /></label>
        <label>To date<input type="date" name="dateTo" value={filters.dateTo} onChange={updateFilter} /></label>
        <button type="button" className="cv-button cv-secondary" onClick={() => setFilters(initialFilters)}>Clear filters</button>
      </div>

      {loading ? <p>Loading results…</p> : null}
      {loadError ? <p className="cv-error" role="alert">{loadError}</p> : null}
      {!loading && !loadError ? (
        <>
          <div className="cv-stats">
            {[
              ["Total responses", metrics.total],
              ["Definitely interested", metrics.definitelyInterested],
              ["Possibly interested", metrics.possiblyInterested],
              ["Overall showing interest", `${metrics.overallInterestPercentage}%`],
              ["Riverside residents", metrics.riversideResidents],
              ["Riverside showing interest", `${metrics.riversideInterestPercentage}%`],
              ["Asked to be contacted", metrics.contactRequested],
            ].map(([label, value]) => <div key={label}><strong>{value}</strong><span>{label}</span></div>)}
          </div>

          <section className="cv-card cv-funding-summary">
            <p className="cv-kicker">Funding Evidence Summary</p>
            <h2>Ready for funding discussions</h2>
            <p className="cv-summary-text">{summary}</p>
            <button type="button" className="cv-button" onClick={copySummary}><ClipboardCopy size={18} /> Copy Summary</button>
            <span className="cv-copy-status" aria-live="polite">{copyState}</span>
          </section>

          <section className="cv-card">
            <h2>CSV exports</h2>
            <p>The anonymised file excludes document IDs, contact details and free-text answers. The full file is for authorised UUN administration only.</p>
            <div className="cv-inline-actions">
              <button type="button" className="cv-button cv-secondary" onClick={() => exportCsv(false)}><Download size={18} /> Anonymised CSV</button>
              <button type="button" className="cv-button" onClick={() => exportCsv(true)}><Download size={18} /> Full authorised CSV</button>
            </div>
          </section>

          <div className="cv-dashboard-grid">
            <Breakdown title="Most popular workshop topics" data={countResponseValues(shown, "topics")} />
            <Breakdown title="Most requested first workshop" data={countResponseValues(shown, "priorityTopic")} />
            <Breakdown title="Interest level" data={countResponseValues(shown, "workshopInterest")} />
            <Breakdown title="AI experience levels" data={countResponseValues(shown, "aiExperience")} />
            <Breakdown title="Digital confidence" data={countResponseValues(shown, "digitalConfidence")} />
            <Breakdown title="Preferred days and times" data={countResponseValues(shown, "preferredTimes")} />
            <Breakdown title="Preferred delivery methods" data={countResponseValues(shown, "deliveryPreference")} />
            <Breakdown title="Key barriers" data={countResponseValues(shown, "barriers")} />
            <Breakdown title="Device access" data={countResponseValues(shown, "deviceAccess")} />
            <Breakdown title="Area breakdown" data={countResponseValues(shown, "area")} />
            <Breakdown title="Age breakdown" data={countResponseValues(shown, "ageGroup")} />
            <Breakdown title="Riverside residents" data={countResponseValues(shown, "riversideResident")} />
            <Breakdown title="Children and young people interest" data={countResponseValues(shown, "youthInterest")} />
            <Breakdown title="Reasons for learning" data={countResponseValues(shown, "motivations")} />
          </div>

          <section className="cv-card">
            <h2>Requested workshops and comments</h2>
            {shown.filter((row) => row.finalComments).length ? shown.filter((row) => row.finalComments).map((row) => (
              <blockquote key={row.id}>{row.finalComments}<small>{row.area} · {row.ageGroup}{row.riversideResident === "Yes" ? " · Riverside resident" : ""}</small></blockquote>
            )) : <p>No written answers in this selection.</p>}
          </section>
        </>
      ) : null}
    </div>
  );
}
